import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import {
  Box, Typography, Skeleton, Button, TextField, Grid, Card, CardContent,
  Avatar, CircularProgress, Paper, Stack, Chip, List, ListItemButton, // Use ListItemButton directly
  Snackbar, Alert, Fade, IconButton, InputAdornment, Tooltip, useMediaQuery,
  Checkbox, FormControlLabel, FormGroup, // Added Checkbox components
  FormHelperText, // To explain roles
  Divider,
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import NoResultsImage from '../../../assets/no-results.png'; // Adjust path as needed
import {
  InfoOutlined as InfoIcon, Search as SearchIcon, GroupAddOutlined as GroupAddIcon,
  CheckCircleOutline as CheckCircleOutlineIcon, CancelOutlined as CancelIcon,
  PersonOutline as PersonIcon, AdminPanelSettingsOutlined as AdminIcon,
  SchoolOutlined as SchoolIcon, WorkOutline as WorkIcon, LibraryBooksOutlined as LibraryIcon,
  ComputerOutlined as ComputerIcon, AttachMoneyOutlined as MoneyIcon, CalculateOutlined as CalculateIcon,
  DriveEtaOutlined as DriveIcon, Close as CloseIcon, PersonOffOutlined as PersonOffIcon,
  Save as SaveIcon, // Use SaveIcon
} from '@mui/icons-material';
import { ADMIN_LAYOUT } from '../../../adminThemeTokens';

// --- Constants ---
const DEBOUNCE_DELAY = 300;

// --- Styled Components ---
const MainPanel = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 2.5), // Vertical, Horizontal padding
  borderRadius: theme.shape.borderRadius * 1.5,
  height: 'calc(100vh - 360px)', // Adjust based on header/footer/margins
  minHeight: 400, // Ensure minimum height
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none', // Cleaner look
  overflow: 'hidden', // Prevent inner content from overflowing Paper
}));

const EmployeeListItemButton = styled(ListItemButton)(({ theme, selected }) => ({
  padding: theme.spacing(1, 1.5), // Compact padding
  marginBottom: theme.spacing(0.75),
  borderRadius: theme.shape.borderRadius, // Standard radius
  transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
  border: `1px solid transparent`, // Placeholder for hover/selected state
  '&:hover': {
    backgroundColor: alpha(theme.palette.action.hover, 0.05), // Subtle hover
    // borderColor: theme.palette.divider, // Optional hover border
  },
  ...(selected && {
    backgroundColor: alpha(theme.palette.primary.light, 0.12), // Clearer selection
    borderColor: alpha(theme.palette.primary.main, 0.6),
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.light, 0.18),
    },
  }),
}));

const ScrollableBox = styled(Box)(({ theme }) => ({
  overflowY: 'auto',
  flexGrow: 1,
  paddingRight: theme.spacing(0.5), // Space for scrollbar
  // Minimalist scrollbar
  '&::-webkit-scrollbar': { width: '5px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(theme.palette.text.secondary, 0.25), borderRadius: '3px' },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: alpha(theme.palette.text.secondary, 0.4) },
}));

const RoleChip = styled(Chip)(({ theme }) => ({
  fontSize: '0.7rem',
  height: '22px',
  fontWeight: 500,
  cursor: 'default',
  borderRadius: theme.shape.borderRadius * 0.75,
  '& .MuiChip-icon': { marginLeft: '5px', marginRight: '-5px', fontSize: '0.9rem' },
}));

const StickyActionBar = styled(Paper)(({ theme }) => ({
  position: 'sticky', // Stick to bottom of its container (MainPanel)
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(1.5, 2.5),
  marginTop: 'auto', // Push to bottom of flex container
  backgroundColor: theme.palette.background.default, // Match main background
  borderTop: `1px solid ${theme.palette.divider}`,
  zIndex: 1, // Ensure it's above the scrollable content within the panel
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
}));

// --- Component ---
const PermissionsSettings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [availableRoles, setAvailableRoles] = useState([]); // Roles that can be assigned via this UI
  const [employees, setEmployees] = useState([]); // Original fetched & processed list
  const [filteredEmployees, setFilteredEmployees] = useState([]); // List displayed after search filter
  const [selectedEmployee, setSelectedEmployee] = useState(null); // The employee object being edited
  const [initialSelectedRoles, setInitialSelectedRoles] = useState([]); // Roles when employee was first selected
  const [rolesBeingAssigned, setRolesBeingAssigned] = useState([]); // Current state of checkboxes
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const searchTimeout = useRef(null); // Use ref for timeout ID

  const currentEmployeeCode = localStorage.getItem('code');
  const schoolId = localStorage.getItem('schoolId'); // Assuming schoolId is needed

  // --- Role Configuration (Centralized definitions) ---
  const roleConfig = useMemo(() => ({
    // Key: Role name (uppercase) from backend/API
    // Value: Object with UI details
    SYSTEM_ADMIN: { icon: <AdminIcon fontSize="inherit" />, color: theme.palette.error.dark, name: 'System Admin', description: 'Platform-level administration' },
    SUPER_ADMIN: { icon: <AdminIcon fontSize="inherit" />, color: theme.palette.error.main, name: 'Super Admin', description: 'Full system access (Use with extreme caution)' },
    APP_ADMIN: { icon: <AdminIcon fontSize="inherit" />, color: theme.palette.error.light, name: 'App Admin', description: 'Cross-school platform administrator' },
    SCHOOL_ADMIN: { icon: <SchoolIcon fontSize="inherit" />, color: theme.palette.primary.main, name: 'School Admin', description: 'Manages school settings, users, configuration' },
    ADMIN: { icon: <AdminIcon fontSize="inherit" />, color: theme.palette.secondary.main, name: 'Administrator', description: 'General administrative tasks' },
    EXAMS_ADMIN: { icon: <LibraryIcon fontSize="inherit" />, color: theme.palette.info.main, name: 'Exams Admin', description: 'Manages exam and result workflows' },
    FINANCE_ADMIN: { icon: <MoneyIcon fontSize="inherit" />, color: theme.palette.warning.main, name: 'Finance Admin', description: 'Manages financial configurations' },
    ACCOUNTANT: { icon: <CalculateIcon fontSize="inherit" />, color: theme.palette.warning.dark, name: 'Accountant', description: 'Handles fee and expense records' },
    IT_ADMIN: { icon: <ComputerIcon fontSize="inherit" />, color: theme.palette.info.dark, name: 'IT Admin', description: 'Manages digital systems and integrations' },
    TEACHER: { icon: <WorkIcon fontSize="inherit" />, color: theme.palette.info.dark, name: 'Teacher', description: 'Teacher' },
    LIBRARIAN: { icon: <LibraryIcon fontSize="inherit" />, color: theme.palette.success.dark, name: 'Librarian', description: 'Manages library operations' },
    COACH: { icon: <WorkIcon fontSize="inherit" />, color: theme.palette.success.main, name: 'Coach', description: 'Manages sports activities' },
    STAFF: { icon: <WorkIcon fontSize="inherit" />, color: theme.palette.grey[700], name: 'Staff', description: 'General operational staff' },
    CLERK: { icon: <PersonIcon fontSize="inherit" />, color: theme.palette.grey[700], name: 'Clerk', description: 'Supports office administration' },
    DRIVER: { icon: <DriveIcon fontSize="inherit" />, color: alpha(theme.palette.secondary.light, 0.9), name: 'Driver', description: 'Manages transportation' },
    EMPLOYEE: { icon: <WorkIcon fontSize="inherit" />, color: theme.palette.info.dark, name: 'Employee', description: 'Employee' },
    // Default for roles not explicitly defined above
    DEFAULT: { icon: <PersonIcon fontSize="inherit" />, color: theme.palette.grey[600], name: 'Unknown Role', description: '' },
  }), [theme]); // Add theme dependency

  const getRoleDetails = useCallback((role) => roleConfig[role] || roleConfig.DEFAULT, [roleConfig]);
  const getRoleContrastText = useCallback((role) => theme.palette.getContrastText(getRoleDetails(role).color), [theme, getRoleDetails]);

  // --- Snackbar Utility ---
  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbarMessage(message); setSnackbarSeverity(severity); setSnackbarOpen(true);
  }, []);

  // --- Data Fetching & Processing ---
  const processEmployeeData = useCallback((data, manageableRolesList) => {
    if (!data) return []; // Handle null/undefined data
    return data
      .filter(emp => emp && emp.employeeCode !== currentEmployeeCode) // Ensure emp exists and filter self
      .map((employee) => {
        let rolesArray = [];
        // Normalize roles from backend (string or array)
        if (Array.isArray(employee.role)) {
          rolesArray = employee.role.map(r => String(r || '').toUpperCase()).filter(Boolean);
        } else if (typeof employee.role === 'string' && employee.role.trim() !== '') {
          rolesArray = employee.role.split(',').map((r) => r.trim().toUpperCase()).filter(Boolean);
        }

        // Filter to only roles manageable by this UI
        const manageableRoles = rolesArray.filter(r => manageableRolesList.includes(r));

        return {
          ...employee,
          id: employee.employeeId || employee.id, // Use employeeId preferentially
          fullName: `${employee.firstName || ''} ${employee.lastName || ''}`.trim(),
          roles: manageableRoles, // Roles assignable in UI
          _originalAllRoles: rolesArray, // All roles from backend
          avatarColor: manageableRoles.length > 0 ? getRoleDetails(manageableRoles[0]).color : theme.palette.grey[500],
          avatarContrastText: manageableRoles.length > 0 ? getRoleContrastText(manageableRoles[0]) : theme.palette.common.white,
        };
      })
       // IMPORTANT: Keep employees who have at least one *assignable* role OR have no roles at all
      .filter(emp => emp.roles.length > 0 || emp._originalAllRoles.length === 0)
      .sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [currentEmployeeCode, getRoleDetails, getRoleContrastText, theme.palette.grey, theme.palette.common.white]); // availableRoles removed, passed as arg

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesResponse, employeesResponse] = await Promise.all([
        axiosInstance.get('/api/roles'), // Endpoint for available roles
        axiosInstance.get(`/api/employees`), // Endpoint for employees
      ]);

      const validManageableRoles = (rolesResponse.data || [])
        .map(r => String(r || '').toUpperCase())
        .filter(role => role !== 'DEFAULT')
        .sort(); // Sort alphabetically for consistent display order
      setAvailableRoles(validManageableRoles);

      // Process employees using the determined validManageableRoles
      const processedEmployees = processEmployeeData(employeesResponse.data, validManageableRoles);
      setEmployees(processedEmployees);
      setFilteredEmployees(processedEmployees); // Initialize filtered list

    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Failed to fetch initial data.', 'error');
      setEmployees([]); // Set to empty arrays on error
      setFilteredEmployees([]);
      setAvailableRoles([]);
    } finally {
      setLoading(false);
    }
  }, [processEmployeeData, roleConfig, showSnackbar]); // processEmployeeData depends on roleConfig

  useEffect(() => {
    fetchData(); // Fetch on mount
     // Cleanup search timeout
     return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [fetchData]); // Run fetchData once on mount

  // --- Change Detection for Action Bar ---
  const hasChanges = useMemo(() => {
    if (!selectedEmployee) return false;
    // Efficiently compare sorted arrays
    const initialSorted = [...initialSelectedRoles].sort();
    const currentSorted = [...rolesBeingAssigned].sort();
    return JSON.stringify(initialSorted) !== JSON.stringify(currentSorted);
  }, [selectedEmployee, initialSelectedRoles, rolesBeingAssigned]);

  // --- Handlers ---
  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
    const manageableRoles = employee.roles || [];
    setRolesBeingAssigned(manageableRoles);
    setInitialSelectedRoles(manageableRoles); // Store initial state
  };

  const handleDeselectEmployee = () => {
    setSelectedEmployee(null);
    setRolesBeingAssigned([]);
    setInitialSelectedRoles([]);
  };

  const handleRoleAssignmentChange = (event) => {
    const role = event.target.name;
    const isChecked = event.target.checked;
    setRolesBeingAssigned(prev =>
      isChecked ? [...prev, role] : prev.filter(r => r !== role)
    );
  };

   const handleSave = async () => {
    if (!selectedEmployee || !hasChanges) return;

    // Combine manageable roles being assigned with original unmanageable roles
    const originalNonManageableRoles = (selectedEmployee._originalAllRoles || []).filter(r => !availableRoles.includes(r));
    const finalRolesPayload = [...new Set([...rolesBeingAssigned, ...originalNonManageableRoles])];

    const payload = { employeeId: selectedEmployee.id, roles: finalRolesPayload };
    setIsSaving(true);

    try {
      await axiosInstance.patch(`/api/employees/${selectedEmployee.id}/roles`, payload);
      showSnackbar('Roles updated successfully!', 'success');
      // Optimistic UI update (or refetch for certainty)
      const updatedEmployees = employees.map(emp =>
        emp.id === selectedEmployee.id ? { ...emp, roles: rolesBeingAssigned, _originalAllRoles: finalRolesPayload } : emp
      );
      setEmployees(updatedEmployees);
      // Re-apply search filter to updated list
      const currentSearch = searchTerm.toLowerCase();
       setFilteredEmployees(updatedEmployees.filter(employee =>
          employee.fullName.toLowerCase().includes(currentSearch) ||
          employee.email?.toLowerCase().includes(currentSearch)
      ));
      handleDeselectEmployee(); // Deselect after save
    } catch (error) {
      console.error('Error saving roles:', error);
      showSnackbar(error.response?.data?.message || 'Failed to update roles.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSearchChange = useCallback((event) => {
    const searchValue = event.target.value;
    setSearchTerm(searchValue);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(() => {
        const lowerCaseSearch = searchValue.toLowerCase();
        setFilteredEmployees(employees.filter(employee =>
            employee.fullName.toLowerCase().includes(lowerCaseSearch) ||
            (employee.email && employee.email.toLowerCase().includes(lowerCaseSearch)) // Check if email exists
        ));
    }, DEBOUNCE_DELAY);

  }, [employees]);

  const clearSearch = () => {
    setSearchTerm('');
    setFilteredEmployees(employees);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // --- Render Helpers ---
  const renderRoleChip = (role, size = "small") => {
     const details = getRoleDetails(role);
     return ( <RoleChip key={role} label={details.name} size={size} icon={React.cloneElement(details.icon, { sx: { fontSize: '1em' }})} sx={{ bgcolor: details.color, color: getRoleContrastText(role), mr: 0.5, mb: 0.5 }} /> );
  };

  const renderEmployeeList = () => { /* ... same basic structure ... */
      if (loading) { /* ... Skeleton ... */
           return ( <Stack spacing={1} sx={{ p: 1 }}>{ [...Array(8)].map((_, i) => <Stack direction="row" spacing={1.5} key={i} alignItems="center"><Skeleton variant="circular" width={36} height={36} /><Box sx={{ flexGrow: 1 }}><Skeleton height={18} width="75%" /><Skeleton height={14} width="50%" /></Box></Stack>) } </Stack> );
      }
      if (filteredEmployees.length === 0) { /* ... No Results ... */
          return ( <Box sx={{ textAlign: 'center', mt: 4, p: 3 }}><img src={NoResultsImage} alt="No Results" style={{ width: '80px', opacity: 0.6, marginBottom: theme.spacing(2) }} /><Typography variant="body1" color="text.secondary">{searchTerm ? 'No employees match.' : 'No employees found.'}</Typography></Box> );
      }
      return (
          <List disablePadding sx={{ pt: 1 }}>
              {filteredEmployees.map((employee) => (
                  <EmployeeListItemButton key={employee.id} selected={selectedEmployee?.id === employee.id} onClick={() => handleEmployeeSelect(employee)}>
                       <Stack direction="row" spacing={1.5} alignItems="center" width="100%">
                           <Avatar sx={{ bgcolor: employee.avatarColor, color: employee.avatarContrastText, width: 36, height: 36, fontSize: '0.9rem', fontWeight: 600 }}>
                               {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0)}
                           </Avatar>
                           <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                               <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>{employee.fullName}</Typography>
                               <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{employee.email}</Typography>
                           </Box>
                       </Stack>
                  </EmployeeListItemButton>
              ))}
          </List>
      );
  };

  // --- Main Render ---
  return (
    <Box sx={{ p: ADMIN_LAYOUT.pagePadding, bgcolor: 'background.default', minHeight: 'calc(100vh - 64px)', maxWidth: '100%' }}>
      {/* Page Header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 3 }} spacing={1}>
          <Box>
              <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AdminIcon fontSize={isMobile ? "medium" : "large"} color="primary" /> Role Management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Assign system roles and permissions to employees.</Typography>
          </Box>
      </Stack>

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Left Panel: Employee List */}
        <Grid item xs={12} md={5} lg={4}>
          <MainPanel>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="action" /> Employees
            </Typography>
            <TextField fullWidth placeholder="Search Name or Email..." value={searchTerm} onChange={handleSearchChange} variant="outlined" size="small" InputProps={{ startAdornment: ( <InputAdornment position="start"><SearchIcon color="action" fontSize="small" /></InputAdornment> ), endAdornment: searchTerm && ( <InputAdornment position="end"><IconButton onClick={clearSearch} edge="end" size="small"><CloseIcon fontSize="small" /></IconButton></InputAdornment> ), sx: { borderRadius: 2 } }} sx={{ mb: 1 }}/>
             {/* Explanation for missing employees */}
             <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
                 Only employees with assignable roles (or no roles assigned yet) are listed here. Contact support if an employee is missing unexpectedly.
             </Typography>
            <ScrollableBox>
              {renderEmployeeList()}
            </ScrollableBox>
          </MainPanel>
        </Grid>

        {/* Right Panel: Role Assignment */}
        <Grid item xs={12} md={7} lg={8}>
          <MainPanel sx={{ position: 'relative' }}>
            <Fade in={!selectedEmployee} timeout={300} unmountOnExit>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', color: 'text.secondary', p: 3 }}>
                <PersonOffIcon sx={{ fontSize: 70, mb: 2, color: theme.palette.grey[300] }} />
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>Select an Employee</Typography>
                <Typography variant="body1">Choose an employee from the list to view or modify their roles.</Typography>
              </Box>
            </Fade>

            <Fade in={!!selectedEmployee} timeout={300} mountOnEnter unmountOnExit>
               <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {selectedEmployee && (
                        <>
                            {/* Selected Employee Header */}
                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} mb={2} pb={2} borderBottom={`1px solid ${theme.palette.divider}`}>
                                {/* ... Employee Avatar and Name ... */}
                                 <Stack direction="row" alignItems="center" spacing={2} overflow="hidden">
                                    <Avatar sx={{ bgcolor: selectedEmployee.avatarColor, color: selectedEmployee.avatarContrastText, width: 48, height: 48, fontSize: '1.1rem', fontWeight: 600 }}>{selectedEmployee.firstName?.charAt(0)}{selectedEmployee.lastName?.charAt(0)}</Avatar>
                                    <Box overflow="hidden">
                                        <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>{selectedEmployee.fullName}</Typography>
                                        <Typography variant="body2" color="text.secondary" noWrap>{selectedEmployee.email}</Typography>
                                    </Box>
                                </Stack>
                                <Tooltip title="Deselect Employee"><IconButton onClick={handleDeselectEmployee}><CloseIcon /></IconButton></Tooltip>
                            </Stack>

                            {/* Assign Roles Section */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <GroupAddIcon fontSize="small" color="primary"/> Assign Roles
                            </Typography>
                            <ScrollableBox sx={{ flexGrow: 1, maxHeight: 'calc(100% - 220px)', pr: 1 }}> {/* Adjust maxHeight based on surrounding elements */}
                                <FormGroup>
                                    {availableRoles.map((role) => {
                                        const details = getRoleDetails(role);
                                        return (
                                            <Tooltip title={details.description || ''} placement="right" key={role} arrow>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox checked={rolesBeingAssigned.includes(role)} onChange={handleRoleAssignmentChange} name={role} size="small" sx={{ color: details.color, '&.Mui-checked': { color: details.color } }} />
                                                    }
                                                    label={
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            {React.cloneElement(details.icon, { sx: { color: details.color } })}
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>{details.name}</Typography>
                                                        </Stack>
                                                    }
                                                    sx={{ borderBottom: `1px dashed ${theme.palette.divider}`, py: 0.5, '&:last-child': { borderBottom: 'none' } }} // Subtle separator
                                                />
                                            </Tooltip>
                                        );
                                    })}
                                </FormGroup>
                            </ScrollableBox>

                             {/* Warnings Section */}
                            <Box sx={{ py: 1, minHeight: 60 }}> {/* Reserve space */}
                                {rolesBeingAssigned.includes('SCHOOL_ADMIN') && !rolesBeingAssigned.includes('SUPER_ADMIN') && (
                                  <Alert severity="warning" icon={<InfoIcon fontSize="inherit" />} sx={{ fontSize: '0.8rem' }}>School Admin: Significant control.</Alert>
                                )}
                                {rolesBeingAssigned.includes('SUPER_ADMIN') && (
                                  <Alert severity="error" icon={<InfoIcon fontSize="inherit" />} sx={{ fontSize: '0.8rem' }}>Super Admin: Unrestricted access!</Alert>
                                )}
                            </Box>

                            {/* Action Buttons - Appear conditionally */}
                             <Fade in={hasChanges}>
                                <StickyActionBar elevation={0} variant="outlined">
                                    <Button variant="text" color="inherit" onClick={handleDeselectEmployee} disabled={isSaving} size="small" sx={{ color: 'text.secondary' }}>Cancel</Button>
                                    <Button variant="contained" color="primary" onClick={handleSave} disabled={isSaving} size="small" startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}>
                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                </StickyActionBar>
                             </Fade>
                        </>
                    )}
                </Box>
            </Fade>
          </MainPanel>
        </Grid>
      </Grid>

      {/* Snackbar Notification */}
      <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} sx={{ bottom: { xs: 70, sm: 20 } }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ width: '100%', boxShadow: theme.shadows[4] }}>{snackbarMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PermissionsSettings;
